import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { del, get, post } from "../api";
import { Sparkline } from "../components/Charts";
import type { Point } from "../components/Charts";
import { IntegrationGuide } from "../components/IntegrationGuide";
import { PaginatedList } from "../components/PaginatedList";
import { SiteNavHeader } from "../components/SiteNavHeader";
import { useI18n } from "../i18n";

interface Quality {
  [k: string]: unknown;
  coverage: string;
  never_collected?: boolean;
  pending_aggregation: number;
  processed_through: string | null;
  provisional: boolean;
  collection_scope?: { mode: string; channels: Record<string, boolean> };
  agent_health?: { source_kind: string; state: string; reported_at: string }[];
  known_gaps?: { count: number; latest_end: string | null };
  versions?: Record<string, number>;
  note?: string;
}

interface QueryResp {
  metric: string;
  granularity: string;
  quality: Quality;
  rule_version?: number;
  rule_version_effective?: number;
  data_scope?: string;
  series?: (Point & { uv?: number })[];
  total?: number;
  total_uv?: number;
  items?: { value: string; count: number }[];
  has_overflow?: boolean;
  total_in_window?: number;
  metrics?: Record<string, {
    sample_count: number;
    p75: string | null;
    approximate: boolean;
    grades?: Record<string, number>;
    low_sample?: boolean;
    note?: string | null;
  }>;
  conversion_rate?: number | null;
  qualified_pv?: number;
  converted_pages?: number;
  note?: string | null;
}

function formatCountryName(code: string, locale: "en" | "zh", unknownLabel: string): string {
  if (!code || code === "__unknown__") return unknownLabel;
  const regionNames = typeof Intl !== "undefined" && (Intl as any).DisplayNames
    ? new (Intl as any).DisplayNames([locale === "zh" ? "zh-CN" : "en"], { type: "region" })
    : null;
  if (regionNames && /^[A-Z]{2}$/.test(code)) {
    try {
      const name = regionNames.of(code);
      if (name) return `${code} (${name})`;
    } catch {
      // fallback
    }
  }
  return code;
}

function getTimeWindow(range: "24h" | "today" | "7d" | "30d"): { start: string; end: string; granularity?: string } {
  const now = new Date();
  const end = new Date(now.getTime() + 5 * 60_000);
  if (range === "24h") {
    const start = new Date(end.getTime() - 24 * 3600_000);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "hour" };
  }
  if (range === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "hour" };
  }
  if (range === "7d") {
    const start = new Date(end.getTime() - 7 * 86400_000);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "day" };
  }
  const start = new Date(end.getTime() - 30 * 86400_000);
  return { start: start.toISOString(), end: end.toISOString(), granularity: "day" };
}

function PaginatedMetricsBarList({
  items,
  overflow,
  color,
  emptyLabel,
  overflowNotice,
}: {
  items: { value: string; count: number }[];
  overflow?: boolean;
  color?: string;
  emptyLabel?: string;
  overflowNotice?: React.ReactNode;
}) {
  const max = Math.max(...(items || []).map((i) => i.count), 1);
  return (
    <PaginatedList
      items={items || []}
      searchKey={(i) => i.value}
      emptyLabel={emptyLabel}
      overflow={overflow}
      overflowNotice={overflowNotice}
      renderItem={(i) => (
        <div className="barrow" key={i.value}>
          <span className="barlabel" title={i.value}>
            {i.value}
          </span>
          <div className="bartrack">
            <div
              className="barfill"
              style={{ width: `${(i.count / max) * 100}%`, backgroundColor: color || "var(--brand)" }}
            />
          </div>
          <span className="barcount">{i.count.toLocaleString()}</span>
        </div>
      )}
    />
  );
}

function PaginatedCountryBarList({
  items,
  overflow,
  locale,
  unknownLabel,
  emptyLabel,
}: {
  items: { value: string; count: number }[];
  overflow?: boolean;
  locale: "en" | "zh";
  unknownLabel: string;
  emptyLabel: string;
}) {
  const max = Math.max(...(items || []).map((i) => i.count), 1);
  return (
    <PaginatedList
      items={items || []}
      searchKey={(i) => `${i.value} ${formatCountryName(i.value, locale, unknownLabel)}`}
      emptyLabel={emptyLabel}
      overflow={overflow}
      renderItem={(i) => (
        <div className="barrow" key={i.value}>
          <span className="barlabel" title={i.value}>
            {formatCountryName(i.value, locale, unknownLabel)}
          </span>
          <div className="bartrack">
            <div
              className="barfill"
              style={{ width: `${(i.count / max) * 100}%`, backgroundColor: "#0284c7" }}
            />
          </div>
          <span className="barcount">{i.count.toLocaleString()}</span>
        </div>
      )}
    />
  );
}

function QualityCard({ quality }: { quality: Quality }) {
  const { t } = useI18n();
  const channels = quality.collection_scope?.channels;
  const health = quality.agent_health ?? [];
  return (
    <div className="card" style={{ padding: "14px 18px", marginBottom: 20 }}>
      <b>{t.siteDetail.qualityCoverage}</b>{" "}
      <span className="muted" style={{ fontSize: 13 }}>
        coverage={quality.coverage}
        {quality.never_collected && ` ${t.siteDetail.qualityNeverCollected}`}
        {typeof quality.versions?.rule === "number" &&
          ` · ${t.siteDetail.ruleVersion}${quality.versions.rule}`}
        {" · "}
        {t.siteDetail.pendingAggregation}{quality.pending_aggregation}
        {quality.processed_through &&
          ` · ${t.siteDetail.watermark}${new Date(quality.processed_through).toLocaleTimeString()}`}
        {quality.provisional && ` · ${t.siteDetail.provisional}`}
      </span>
      {channels && (
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
          {t.siteDetail.collectionMode}{quality.collection_scope?.mode};
          {" "}{t.siteDetail.channelsLabel}origin={String(channels.origin)} / browser={String(channels.browser)}
          {health.length > 0 &&
            ` · ${t.siteDetail.agentStatus}${health.map((h) => `${h.source_kind}=${h.state}`).join(", ")}`}
          {quality.known_gaps && quality.known_gaps.count > 0 &&
            ` · ${t.siteDetail.knownGaps}${quality.known_gaps.count} (${quality.known_gaps.latest_end ?? "?"})`}
        </p>
      )}
    </div>
  );
}

export function SiteDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { workspaceId, siteId } = useParams<{ workspaceId: string; siteId: string }>();
  const [searchParams] = useSearchParams();

  // Determine current active view from search query
  const viewParam = searchParams.get("view");
  const tabParam = searchParams.get("tab");
  const currentView = viewParam || (tabParam === "sources" ? "integration" : "overview");

  const [workspaceName, setWorkspaceName] = useState("");
  const [site, setSite] = useState<{ name: string; mode: string } | null>(null);
  const [timeRange, setTimeRange] = useState<"24h" | "today" | "7d" | "30d">("24h");

  // Query states
  const [browser, setBrowser] = useState<QueryResp | null>(null);
  const [origin, setOrigin] = useState<QueryResp | null>(null);
  const [paths, setPaths] = useState<QueryResp | null>(null);
  const [referrers, setReferrers] = useState<QueryResp | null>(null);
  const [countries, setCountries] = useState<QueryResp | null>(null);
  const [rum, setRum] = useState<QueryResp | null>(null);
  const [conv, setConv] = useState<QueryResp | null>(null);
  const [html, setHtml] = useState<QueryResp | null>(null);
  const [browsers, setBrowsers] = useState<QueryResp | null>(null);
  const [devices, setDevices] = useState<QueryResp | null>(null);
  const [channels, setChannels] = useState<QueryResp | null>(null);
  const [aiSources, setAiSources] = useState<QueryResp | null>(null);
  const [entryPages, setEntryPages] = useState<QueryResp | null>(null);
  const [botSummary, setBotSummary] = useState<{
    total_bot_requests: number;
    bots: { name: string; count: number }[];
    top_crawled_pages: { path: string; count: number }[];
  } | null>(null);
  const [sessions, setSessions] = useState<{
    total_sessions: number;
    unique_visitors: number;
    bounced_sessions: number;
    bounce_rate: number;
    avg_pages_per_session: number;
    avg_session_duration_seconds: number;
  } | null>(null);
  const [realtime, setRealtime] = useState<{
    active_visitors: number;
    recent_pageviews: { path: string; occurred_at: string; country?: string }[];
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Modals & export
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState<"csv" | "json">("csv");
  const [exportKind, setExportKind] = useState<"events" | "metrics">("events");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportResult, setExportResult] = useState<{
    job_id: string; status: string; download_token: string; expires_at: string;
  } | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId || !siteId) return;
    const win = getTimeWindow(timeRange);
    const q = (metric: string, extra: Record<string, unknown> = {}) =>
      post<QueryResp>(`/api/v1/sites/${siteId}/query`, {
        metric,
        start: win.start,
        end: win.end,
        ...(win.granularity ? { granularity: win.granularity } : {}),
        ...extra,
      });

    try {
      const [wsData, sitesData] = await Promise.all([
        get<{ name: string }>(`/api/v1/workspaces/${workspaceId}`).catch(() => null),
        get<{ items: { id: string; name: string; mode: string }[] }>(`/api/v1/workspaces/${workspaceId}/sites`),
      ]);

      if (wsData) setWorkspaceName(wsData.name);
      const curSite = sitesData.items.find((s) => s.id === siteId) ?? null;
      setSite(curSite);

      const [b, o, p, rf, c, r, cv, hd, br, dv, ch, ai, ep, bs, sess, rt] = await Promise.all([
        q("events_total", { source_kind: "browser" }).catch(() => null),
        q("events_total", { source_kind: "origin" }).catch(() => null),
        q("top_paths", { source_kind: "origin" }).catch(() => null),
        q("top_referrers", { source_kind: "browser" }).catch(() => null),
        q("top_countries", { source_kind: "browser" }).catch(() => null),
        q("rum_summary").catch(() => null),
        q("conversion_summary").catch(() => null),
        q("html_documents", { source_kind: "origin" }).catch(() => null),
        q("top_browsers", { source_kind: "browser" }).catch(() => null),
        q("top_devices", { source_kind: "browser" }).catch(() => null),
        q("top_channels", { source_kind: "browser" }).catch(() => null),
        q("top_ai_sources", { source_kind: "browser" }).catch(() => null),
        q("top_entry_pages").catch(() => null),
        q("bot_summary").catch(() => null),
        q("sessions_summary", { source_kind: "browser" }).catch(() => null),
        get<{ active_visitors: number; recent_pageviews: { path: string; occurred_at: string; country?: string }[] }>(
          `/api/v1/sites/${siteId}/realtime/visitors?stream=false`
        ).catch(() => null),
      ]);

      if (b) setBrowser(b);
      if (o) setOrigin(o);
      if (p) setPaths(p);
      if (rf) setReferrers(rf);
      if (c) setCountries(c);
      if (r) setRum(r);
      if (cv) setConv(cv);
      if (hd) setHtml(hd);
      if (br) setBrowsers(br);
      if (dv) setDevices(dv);
      if (ch) setChannels(ch);
      if (ai) setAiSources(ai);
      if (ep) setEntryPages(ep);
      if (bs) setBotSummary(bs as any);
      if (sess) setSessions(sess as any);
      if (rt) setRealtime(rt);

      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [workspaceId, siteId, timeRange]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  async function handleDeleteSite() {
    if (!siteId) return;
    setDeleteBusy(true);
    setDeleteErr(null);
    try {
      await del(`/api/v1/sites/${siteId}`);
      setShowDeleteModal(false);
      navigate(`/w/${workspaceId}`);
    } catch (e) {
      setDeleteErr((e as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  }

  if (!site && err) {
    return (
      <div className="center card" style={{ maxWidth: 460, margin: "40px auto", textAlign: "center" }}>
        <p className="error" style={{ marginBottom: 16 }}>{err}</p>
        <button className="primary" onClick={() => load()}>{t.siteDetail.reload}</button>
      </div>
    );
  }

  if (!site) {
    return <div className="muted" style={{ padding: 40, textAlign: "center" }}>{t.common.loading}</div>;
  }

  // Get view-specific title and subtitle
  let viewTitle: string = t.nav.overview;
  let viewSubtitle: string = locale === "zh" ? "实时访客、核心流量走势与质量监控。" : "Real-time visitors, key traffic trends, and quality watermark.";

  if (currentView === "sources_traffic") {
    viewTitle = t.nav.sources;
    viewSubtitle = locale === "zh" ? "外部来源域名、流量渠道及生成式 AI 搜索推荐分布。" : "External referrers, traffic channels, and generative AI search trends.";
  } else if (currentView === "content") {
    viewTitle = t.nav.content;
    viewSubtitle = locale === "zh" ? "全站热门受访 URL、入口着陆页与页面转化率分析。" : "Top visited paths, initial landing pages, and page conversion rates.";
  } else if (currentView === "audience") {
    viewTitle = t.nav.audience;
    viewSubtitle = locale === "zh" ? "地理国家分布、访客终端设备与浏览器家族构成。" : "Geographical countries, client device categories, and browser families.";
  } else if (currentView === "bots") {
    viewTitle = t.nav.bots;
    viewSubtitle = locale === "zh" ? "搜索引擎爬虫、商业机器人抓取频次与高频页面分布。" : "Search engine spiders and AI bot requests and top crawled paths.";
  } else if (currentView === "vitals") {
    viewTitle = t.nav.vitals;
    viewSubtitle = locale === "zh" ? "真实用户体验监控 (RUM)：Google Web Vitals 核心指标与评分等级。" : "Real User Monitoring (RUM): Google Web Vitals performance benchmarks.";
  } else if (currentView === "integration") {
    viewTitle = t.nav.integration;
    viewSubtitle = locale === "zh" ? "浏览器探针 pub_key、服务端 Agent Key 与 Nginx/Node.js 接入指引。" : "Browser probe key, Origin Agent key, and full code integration.";
  } else if (currentView === "settings") {
    viewTitle = t.nav.settings;
    viewSubtitle = locale === "zh" ? "数据导出、采集模式查看与站点生命周期管理。" : "Data export, site ingestion mode, and danger zone.";
  }

  return (
    <div>
      <SiteNavHeader
        workspaceId={workspaceId!}
        workspaceName={workspaceName}
        siteId={siteId!}
        siteName={site.name}
        title={viewTitle}
        subtitle={viewSubtitle}
        timeRange={currentView !== "integration" && currentView !== "settings" ? timeRange : undefined}
        onTimeRangeChange={setTimeRange}
        onRefresh={load}
        onExport={() => {
          setShowExport(true);
          setExportResult(null);
          setExportErr(null);
        }}
      />

      {err && (
        <div style={{
          backgroundColor: "#fef2f2",
          border: "1px solid #f87171",
          color: "#b91c1c",
          padding: "10px 14px",
          borderRadius: 8,
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <span>{err}</span>
          <button className="ghost" style={{ fontSize: 12, padding: "2px 8px", color: "#b91c1c" }} onClick={() => load()}>
            {t.siteDetail.retry}
          </button>
        </div>
      )}

      {/* =========================================================================
          VIEW 1: OVERVIEW (仪表盘概览)
          ========================================================================= */}
      {currentView === "overview" && (
        <>
          <QualityCard quality={browser?.quality ?? origin?.quality ?? {
            coverage: "unknown", pending_aggregation: 0, processed_through: null, provisional: false,
          }} />

          {/* Top 4 Stats Grid (Open Codex style) */}
          <div className="oc-stats-grid">
            {/* Stat 1: Live Active Visitors */}
            <div className="oc-stat-card">
              <div className="oc-stat-label">
                <span>{t.siteDetail.realtimeLiveTitle}</span>
                <span className="badge ok" style={{ fontSize: 11 }}>
                  <span className="status-dot" style={{ width: 6, height: 6 }} /> {t.siteDetail.realtimeLiveBadge}
                </span>
              </div>
              <div className="oc-stat-value" style={{ color: "var(--ok)" }}>
                {realtime?.active_visitors ?? 0}
              </div>
              <div className="oc-stat-footer">
                {t.siteDetail.activeVisitorsCount}
              </div>
            </div>

            {/* Stat 2: Total PV */}
            <div className="oc-stat-card">
              <div className="oc-stat-label">
                <span>{t.siteDetail.totalPv}</span>
                <span className="badge" style={{ fontSize: 11 }}>{timeRange}</span>
              </div>
              <div className="oc-stat-value" style={{ color: "#7c3aed" }}>
                {browser?.total?.toLocaleString() ?? 0}
              </div>
              <div className="oc-stat-footer">
                PV / UV 比: {(browser?.total && (browser?.total_uv || sessions?.unique_visitors))
                  ? (browser.total / (browser.total_uv || sessions?.unique_visitors || 1)).toFixed(2)
                  : "—"}
              </div>
            </div>

            {/* Stat 3: Unique Visitors */}
            <div className="oc-stat-card">
              <div className="oc-stat-label">
                <span>{t.siteDetail.uniqueVisitors}</span>
              </div>
              <div className="oc-stat-value" style={{ color: "var(--brand)" }}>
                {browser?.total_uv ?? (sessions?.unique_visitors ?? "—")}
              </div>
              <div className="oc-stat-footer">
                独立访问用户数 (UV)
              </div>
            </div>

            {/* Stat 4: Bounce Rate & Avg Duration */}
            <div className="oc-stat-card">
              <div className="oc-stat-label">
                <span>{t.siteDetail.bounceRate}</span>
              </div>
              <div className="oc-stat-value" style={{ color: (sessions?.bounce_rate ?? 0) > 0.5 ? "var(--danger)" : "var(--ok)" }}>
                {sessions ? `${(sessions.bounce_rate * 100).toFixed(1)}%` : "—"}
              </div>
              <div className="oc-stat-footer">
                {sessions ? `${sessions.avg_pages_per_session} 页/会话 · ${Math.round(sessions.avg_session_duration_seconds)} 秒` : "30分钟会话窗口"}
              </div>
            </div>
          </div>

          {/* Traffic Trends (Dual Sparklines) */}
          <div className="grid2" style={{ marginBottom: 20 }}>
            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>{t.siteDetail.browserTrafficTitle}</h3>
                <span className="badge" style={{ background: "#ede9fe", color: "#6d28d9" }}>
                  {browser?.granularity || "hour"}
                </span>
              </div>
              {browser && (
                <>
                  <Sparkline points={browser.series ?? []} color="#7c3aed" />
                  <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                    {browser.total?.toLocaleString() ?? 0} PV · {browser.total_uv != null ? `${browser.total_uv} UV · ` : ""}
                    {browser.data_scope}
                  </p>
                </>
              )}
            </div>

            <div className="card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>{t.siteDetail.originRequestsTitle}</h3>
                <span className="badge">{origin?.granularity || "hour"}</span>
              </div>
              {origin && (
                <>
                  <Sparkline points={origin.series ?? []} />
                  <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
                    {origin.total?.toLocaleString() ?? 0} · {t.siteDetail.htmlDocsNotice} {html?.total?.toLocaleString() ?? 0}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Live Recent Pageviews with PaginatedList */}
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>
                ⚡ {locale === "zh" ? "实时访问流 (Live Stream)" : "Live Pageview Stream"}
              </h3>
              <span className="muted" style={{ fontSize: 12 }}>
                {realtime?.recent_pageviews.length ?? 0} {locale === "zh" ? "条近期动态" : "recent hits"}
              </span>
            </div>

            <PaginatedList
              items={realtime?.recent_pageviews ?? []}
              pageSize={6}
              searchKey={(pv) => `${pv.path} ${pv.country || ""}`}
              emptyLabel={t.siteDetail.noRecentVisits}
              renderItem={(pv, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 4px", borderBottom: "1px dashed var(--line-light)" }}>
                  <span style={{ fontFamily: "monospace", fontSize: 13, color: "var(--ink)" }}>{pv.path}</span>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    {pv.country && <span className="badge" style={{ fontSize: 11 }}>{pv.country}</span>}
                    <span className="muted" style={{ fontSize: 12 }}>{new Date(pv.occurred_at).toLocaleTimeString()}</span>
                  </div>
                </div>
              )}
            />
          </div>
        </>
      )}

      {/* =========================================================================
          VIEW 2: SOURCES & TRAFFIC (流量来源与渠道)
          ========================================================================= */}
      {currentView === "sources_traffic" && (
        <>
          <div className="grid2">
            <div className="card">
              <h3>{t.siteDetail.topReferrersTitle}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {t.siteDetail.topReferrersNote}
              </p>
              <PaginatedMetricsBarList
                items={referrers?.items ?? []}
                overflow={referrers?.has_overflow}
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>

            <div className="card">
              <h3>{t.siteDetail.trafficChannelsTitle}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {t.siteDetail.channelsNote}
              </p>
              <PaginatedMetricsBarList
                items={channels?.items ?? []}
                overflow={channels?.has_overflow}
                color="#059669"
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h3>{t.siteDetail.aiTrafficTitle}</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
              {t.siteDetail.aiTrafficNote}
            </p>
            <PaginatedMetricsBarList
              items={aiSources?.items ?? []}
              overflow={aiSources?.has_overflow}
              color="#8b5cf6"
              emptyLabel={locale === "zh" ? "暂未探测到生成式 AI 搜索推荐流量" : "No AI traffic recorded yet"}
            />
          </div>
        </>
      )}

      {/* =========================================================================
          VIEW 3: CONTENT & PAGES (页面与内容转化)
          ========================================================================= */}
      {currentView === "content" && (
        <>
          <div className="grid2">
            <div className="card">
              <h3>{t.siteDetail.topPathsTitle}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {paths?.note || (locale === "zh" ? "由服务端上报或反向代理收集的原始请求路径。" : "Raw request paths routed to origin.")}
              </p>
              <PaginatedMetricsBarList
                items={paths?.items ?? []}
                overflow={paths?.has_overflow}
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>

            <div className="card">
              <h3>{t.siteDetail.topLandingPagesTitle}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {t.siteDetail.landingPagesNote}
              </p>
              <PaginatedMetricsBarList
                items={entryPages?.items ?? []}
                overflow={entryPages?.has_overflow}
                color="#0284c7"
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>
          </div>

          <div className="card" style={{ marginTop: 20 }}>
            <h3>{t.siteDetail.conversionsTitle}</h3>
            {conv ? (
              <div>
                <p style={{ fontSize: 24, fontWeight: "bold", margin: "8px 0" }}>
                  {conv.conversion_rate == null
                    ? t.siteDetail.noQualifiedPv
                    : `${(conv.conversion_rate * 100).toFixed(1)}%`}
                  <span className="muted" style={{ fontSize: 14, fontWeight: "normal", marginLeft: 8 }}>
                    ({conv.converted_pages ?? 0} / {conv.qualified_pv ?? 0} {locale === "zh" ? "次有效页面到达" : "qualified page arrivals"})
                  </span>
                </p>
                <p className="muted" style={{ fontSize: 13 }}>{conv.note}</p>
              </div>
            ) : (
              <div className="empty-chart">{locale === "zh" ? "暂无转化数据" : "No conversion data"}</div>
            )}
          </div>
        </>
      )}

      {/* =========================================================================
          VIEW 4: AUDIENCE & TECH (访客与设备环境)
          ========================================================================= */}
      {currentView === "audience" && (
        <>
          <div className="card">
            <h3>{t.siteDetail.topCountriesTitle}</h3>
            <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
              {t.siteDetail.topCountriesNote}
            </p>
            <PaginatedCountryBarList
              items={countries?.items ?? []}
              overflow={countries?.has_overflow}
              locale={locale}
              unknownLabel={t.siteDetail.unknownLocation}
              emptyLabel={t.siteDetail.emptyChart}
            />
          </div>

          <div className="grid2" style={{ marginTop: 20 }}>
            <div className="card">
              <h3>{t.siteDetail.deviceType}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {t.siteDetail.devicesNote}
              </p>
              <PaginatedMetricsBarList
                items={devices?.items ?? []}
                overflow={devices?.has_overflow}
                color="#f59e0b"
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>

            <div className="card">
              <h3>{t.siteDetail.browserFamily}</h3>
              <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 12 }}>
                {locale === "zh" ? "基于客户端 User-Agent 轻量解析的浏览器品牌统计。" : "Browser family distribution based on User-Agent."}
              </p>
              <PaginatedMetricsBarList
                items={browsers?.items ?? []}
                overflow={browsers?.has_overflow}
                color="#6366f1"
                emptyLabel={t.siteDetail.emptyChart}
              />
            </div>
          </div>
        </>
      )}

      {/* =========================================================================
          VIEW 5: BOTS & SEO (爬虫与机器人分析)
          ========================================================================= */}
      {currentView === "bots" && (
        <>
          <div className="oc-stat-card" style={{ marginBottom: 20 }}>
            <div className="oc-stat-label">
              <span>{t.siteDetail.totalBotRequests}</span>
              <span className="badge warn">Bot Ingestion</span>
            </div>
            <div className="oc-stat-value" style={{ color: "#d97706" }}>
              {botSummary?.total_bot_requests?.toLocaleString() ?? 0}
            </div>
            <div className="oc-stat-footer">
              {t.siteDetail.botsNote}
            </div>
          </div>

          {botSummary ? (
            <div className="grid2">
              <div className="card">
                <h3>{t.siteDetail.topBots}</h3>
                <PaginatedMetricsBarList
                  items={botSummary.bots.map((b) => ({ value: b.name, count: b.count }))}
                  color="#d97706"
                  emptyLabel={t.siteDetail.noBotData}
                />
              </div>

              <div className="card">
                <h3>{t.siteDetail.topCrawledPages}</h3>
                <PaginatedMetricsBarList
                  items={botSummary.top_crawled_pages.map((p) => ({ value: p.path, count: p.count }))}
                  color="#ea580c"
                  emptyLabel={t.siteDetail.noBotData}
                />
              </div>
            </div>
          ) : (
            <div className="empty-chart">{t.siteDetail.noBotData}</div>
          )}
        </>
      )}

      {/* =========================================================================
          VIEW 6: WEB VITALS & RUM (性能体验监控)
          ========================================================================= */}
      {currentView === "vitals" && (
        <div className="card">
          <h3>{t.siteDetail.rumTitle}</h3>
          <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>
            {t.siteDetail.rumNote}
          </p>

          {rum?.metrics && Object.keys(rum.metrics).length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
              {Object.entries(rum.metrics).map(([name, m]) => (
                <div key={name} className="oc-card" style={{ margin: 0, padding: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <b>{name}</b>
                    <span className="badge" style={{ fontSize: 11 }}>
                      {locale === "zh" ? `样本: ${m.sample_count}` : `samples: ${m.sample_count}`}
                    </span>
                  </div>

                  {m.sample_count === 0 ? (
                    <p className="muted" style={{ margin: "12px 0 0", fontSize: 13 }}>
                      {t.siteDetail.rumNoSamples}
                    </p>
                  ) : (
                    <>
                      <p style={{ fontSize: 24, fontWeight: "bold", margin: "10px 0 6px", color: "var(--brand)" }}>
                        p75 ≈ {m.p75}
                      </p>
                      {m.grades && (
                        <div style={{ display: "flex", gap: 6, fontSize: 12, flexWrap: "wrap", marginTop: 8 }}>
                          <span className="badge ok">{t.siteDetail.rumGood}: {m.grades.good}</span>
                          <span className="badge warn">{t.siteDetail.rumNeedsImprovement}: {m.grades.needs_improvement}</span>
                          <span className="badge danger">{t.siteDetail.rumPoor}: {m.grades.poor}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-chart">{locale === "zh" ? "暂无真实用户体验指标采样" : "No RUM metrics collected"}</div>
          )}
        </div>
      )}

      {/* =========================================================================
          VIEW 7: INTEGRATION & KEYS (接入指南与密钥)
          ========================================================================= */}
      {currentView === "integration" && (
        <IntegrationGuide
          siteId={siteId!}
          siteName={site.name}
          workspaceId={workspaceId!}
        />
      )}

      {/* =========================================================================
          VIEW 8: SETTINGS & DANGER ZONE (站点设置)
          ========================================================================= */}
      {currentView === "settings" && (
        <>
          <div className="card">
            <h3>{locale === "zh" ? "站点基础信息" : "Site General Information"}</h3>
            <p>
              <b>{t.common.name}:</b> {site.name}
            </p>
            <p>
              <b>{t.siteDetail.collectionMode}</b>{" "}
              <span className="badge ok">{site.mode}</span>
            </p>
            <p className="muted" style={{ fontSize: 13 }}>
              {t.siteDetail.modeNotice}
            </p>
          </div>

          <div className="card">
            <h3>{t.siteDetail.exportModalTitle}</h3>
            <p className="muted" style={{ fontSize: 13 }}>
              {locale === "zh"
                ? "支持将指定时间窗口内的原始请求事件明细或聚合指标打包为 CSV / JSON 下载。"
                : "Export raw event rows or aggregated metrics as CSV or JSON format."}
            </p>
            <button className="primary" onClick={() => {
              setShowExport(true);
              setExportResult(null);
              setExportErr(null);
            }}>
              {t.siteDetail.exportBtn}
            </button>
          </div>

          {/* Danger Zone */}
          <div className="card" style={{ borderColor: "#fca5a5", marginTop: 24, background: "#fff" }}>
            <h3 style={{ color: "var(--danger)" }}>{t.siteDetail.dangerZoneTitle}</h3>
            <p className="muted" style={{ fontSize: 13 }}>
              {t.siteDetail.deleteSiteNotice}
            </p>
            <button
              style={{ background: "var(--danger)", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}
              onClick={() => {
                setShowDeleteModal(true);
                setDeleteErr(null);
              }}
            >
              {t.siteDetail.deleteSiteBtn}
            </button>
          </div>
        </>
      )}

      {/* Export Modal */}
      {showExport && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div className="card" style={{ maxWidth: 460, width: "90%", background: "#fff" }}>
            <h3>{t.siteDetail.exportModalTitle}</h3>
            {exportErr && <p className="error">{exportErr}</p>}

            {!exportResult ? (
              <>
                <label>{t.siteDetail.exportDataType}</label>
                <select value={exportKind} onChange={(e) => setExportKind(e.target.value as "events" | "metrics")}
                        style={{ width: "100%", padding: 8, marginBottom: 12 }}>
                  <option value="events">{t.siteDetail.exportOptionEvents}</option>
                  <option value="metrics">{t.siteDetail.exportOptionMetrics}</option>
                </select>

                <label>{t.siteDetail.exportFormatLabel}</label>
                <div className="row" style={{ marginBottom: 16 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="fmt" checked={exportFormat === "csv"} onChange={() => setExportFormat("csv")} />
                    CSV
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginLeft: 16 }}>
                    <input type="radio" name="fmt" checked={exportFormat === "json"} onChange={() => setExportFormat("json")} />
                    JSON (UTF-8)
                  </label>
                </div>

                <p className="muted" style={{ fontSize: 12 }}>
                  {t.siteDetail.exportCsvSafeNotice}
                </p>

                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <button className="primary" disabled={exportBusy} onClick={async () => {
                    setExportBusy(true); setExportErr(null);
                    try {
                      const res = await post<{
                        job_id: string; status: string; download_token: string; expires_at: string;
                      }>(`/api/v1/sites/${siteId}/export`, {
                        format: exportFormat,
                        kind: exportKind,
                      });
                      setExportResult(res);
                    } catch (e) {
                      setExportErr((e as Error).message);
                    } finally {
                      setExportBusy(false);
                    }
                  }}>
                    {exportBusy ? t.siteDetail.exportGenerating : t.siteDetail.exportStartBtn}
                  </button>
                  <button className="ghost" onClick={() => setShowExport(false)}>{t.common.cancel}</button>
                </div>
              </>
            ) : (
              <div>
                <p className="badge ok">{t.siteDetail.exportReady}</p>
                <p>{t.siteDetail.exportJobId}<code>{exportResult.job_id}</code></p>
                <p>{t.siteDetail.exportToken}<code>{exportResult.download_token}</code></p>
                <p className="muted" style={{ fontSize: 12 }}>{t.siteDetail.exportExpires}</p>

                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <a href={`/api/v1/export/download/${exportResult.download_token}`} download
                     className="primary" style={{ display: "inline-block", padding: "8px 16px", borderRadius: 8, textDecoration: "none", color: "#fff", background: "var(--brand)" }}>
                    {t.siteDetail.downloadFileBtn} ({exportFormat.toUpperCase()})
                  </a>
                  <button className="ghost" onClick={() => setShowExport(false)}>{t.siteDetail.closeBtn}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Site Modal */}
      {showDeleteModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000,
        }}>
          <div className="card" style={{ maxWidth: 460, width: "90%", background: "#fff" }}>
            <h3 style={{ color: "var(--danger)" }}>{t.siteDetail.deleteSiteConfirmTitle}: {site?.name}</h3>
            <p className="muted">{t.siteDetail.deleteSiteConfirmPrompt}</p>
            {deleteErr && <p className="error">{deleteErr}</p>}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                style={{ background: "var(--danger)", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}
                disabled={deleteBusy}
                onClick={handleDeleteSite}
              >
                {deleteBusy ? t.common.submitting : t.common.delete}
              </button>
              <button className="ghost" onClick={() => setShowDeleteModal(false)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
